import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Gift, Plus, Copy, Check, Send, Ban, Search, Loader2, Mail, AlertCircle,
} from "lucide-react";
import client from "../../api";
import { useCatalog } from "../../context/CatalogContext";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { useToast } from "../../hooks/use-toast";

const detailOf = (err) =>
  err?.response?.data?.detail || err?.message || "Something went wrong";

const inputCls =
  "w-full h-10 px-3 rounded-lg bg-secondary border border-border text-sm outline-none focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/20 transition-all";

const STATUS = {
  pending:  { label: "Not used yet", cls: "bg-brand-500/15 text-brand-400 border-brand-500/30" },
  redeemed: { label: "Redeemed",     cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  expired:  { label: "Expired",      cls: "bg-secondary text-muted-foreground border-border" },
  revoked:  { label: "Cancelled",    cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};

/** Copy that reports back, because a copy button with no feedback is a mystery. */
function CopyButton({ value, label = "Copy link" }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard is blocked outside a secure context or without permission.
      const el = document.createElement("textarea");
      el.value = value;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      el.remove();
    }
    setDone(true);
    setTimeout(() => setDone(false), 1800);
  };
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-secondary border border-border text-xs font-medium hover:border-brand-500/50 transition-colors"
    >
      {done ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      {done ? "Copied" : label}
    </button>
  );
}

export default function AdminRedemptions() {
  const { adminBooks: books } = useCatalog();
  const { toast } = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState(null);

  const [form, setForm] = useState({ email: "", note: "", bookIds: [], send: true });
  const [bookQuery, setBookQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/redemptions");
      setRows(res.data || []);
    } catch (err) {
      toast({ title: "Could not load codes", description: detailOf(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.code.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      r.note.toLowerCase().includes(q) ||
      r.books.some((b) => b.title_en.toLowerCase().includes(q))
    );
  }, [rows, query]);

  const bookOptions = useMemo(() => {
    const q = bookQuery.trim().toLowerCase();
    const list = q
      ? books.filter((b) => b.title_en.toLowerCase().includes(q) || b.author.toLowerCase().includes(q))
      : books;
    return list.slice(0, 40);
  }, [books, bookQuery]);

  const toggleBook = (id) =>
    setForm((f) => ({
      ...f,
      bookIds: f.bookIds.includes(id) ? f.bookIds.filter((x) => x !== id) : [...f.bookIds, id],
    }));

  const create = async () => {
    if (!form.bookIds.length) {
      toast({ title: "Pick at least one audiobook", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const res = await client.post("/admin/redemptions", {
        email: form.email.trim(),
        note: form.note.trim(),
        bookIds: form.bookIds,
        send: form.send,
      });
      setIssued(res.data);
      setCreating(false);
      setForm({ email: "", note: "", bookIds: [], send: true });
      setBookQuery("");
      await load();
    } catch (err) {
      toast({ title: "Could not create the code", description: detailOf(err), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const resend = async (row) => {
    try {
      await client.post(`/admin/redemptions/${row.code}/send`);
      toast({ title: `Sent to ${row.email}` });
      await load();
    } catch (err) {
      toast({ title: "Could not send it", description: detailOf(err), variant: "destructive" });
    }
  };

  const cancel = async (row) => {
    if (!window.confirm(`Cancel ${row.code}? The buyer will no longer be able to use it.`)) return;
    try {
      await client.delete(`/admin/redemptions/${row.code}`);
      toast({ title: `${row.code} cancelled` });
      await load();
    } catch (err) {
      toast({ title: "Could not cancel it", description: detailOf(err), variant: "destructive" });
    }
  };

  return (
    <div className="animate-float-up">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Redemption codes</h1>
          <p className="text-sm text-muted-foreground">
            For audiobooks sold outside the store. {rows.length} issued.
          </p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-full"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New code
        </Button>
      </div>

      <div className="relative my-5 max-w-sm">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <label htmlFor="rd-search" className="sr-only">Search codes</label>
        <input
          id="rd-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Code, email, note or title…"
          className="w-full h-10 pl-10 pr-4 rounded-xl bg-secondary border border-border text-sm outline-none focus:border-brand-500/60"
        />
      </div>

      {loading ? (
        <div className="py-20 text-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-20 text-center">
          <Gift className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">
            {rows.length === 0
              ? "No codes yet. Issue one when someone buys an audiobook elsewhere."
              : "No codes match that search."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <div key={r.code} className="rounded-xl border border-border bg-card/50 p-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-sm font-semibold tracking-wider">{r.code}</span>
                <Badge variant="outline" className={STATUS[r.status]?.cls}>
                  {STATUS[r.status]?.label ?? r.status}
                </Badge>
                {r.email && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" /> {r.email}
                    {r.sentAt ? " · sent" : " · not sent"}
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <CopyButton value={r.url} />
                  {r.status === "pending" && r.email && (
                    <button
                      onClick={() => resend(r)}
                      title="Send the email again"
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-secondary border border-border text-xs font-medium hover:border-brand-500/50 transition-colors"
                    >
                      <Send className="w-3.5 h-3.5" /> Resend
                    </button>
                  )}
                  {r.status === "pending" && (
                    <button
                      onClick={() => cancel(r)}
                      title="Cancel this code"
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-secondary border border-border text-xs font-medium text-muted-foreground hover:text-red-400 hover:border-red-500/40 transition-colors"
                    >
                      <Ban className="w-3.5 h-3.5" /> Cancel
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>{r.books.map((b) => b.title_en).join(" · ") || "no audiobooks"}</span>
                {r.note && <span className="italic">“{r.note}”</span>}
                <span className="ml-auto">
                  {r.status === "redeemed" && r.redeemedAt
                    ? `Redeemed ${r.redeemedAt.slice(0, 10)}`
                    : `Issued ${r.createdAt.slice(0, 10)}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* create */}
      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">New redemption code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label htmlFor="rd-email" className="text-xs font-medium text-muted-foreground">
                Buyer's email <span className="font-normal">(optional, leave blank to send it yourself)</span>
              </label>
              <input
                id="rd-email" type="email" className={inputCls} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="buyer@example.com"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="rd-note" className="text-xs font-medium text-muted-foreground">
                Note <span className="font-normal">(only you see this)</span>
              </label>
              <input
                id="rd-note" className={inputCls} value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Etsy order 3041"
              />
            </div>

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                Audiobooks {form.bookIds.length > 0 && `(${form.bookIds.length} selected)`}
              </span>
              <input
                className={inputCls} value={bookQuery}
                onChange={(e) => setBookQuery(e.target.value)}
                placeholder="Filter by title or author…"
                aria-label="Filter audiobooks"
              />
              <div className="max-h-56 overflow-y-auto rounded-lg border border-border divide-y divide-border/60">
                {bookOptions.map((b) => {
                  const on = form.bookIds.includes(b.id);
                  return (
                    <button
                      key={b.id} type="button" onClick={() => toggleBook(b.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${on ? "bg-brand-500/10" : "hover:bg-secondary/60"}`}
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-brand-500 border-brand-500" : "border-border"}`}>
                        {on && <Check className="w-3 h-3 text-brand-950" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm truncate">{b.title_en}</span>
                        <span className="block text-xs text-muted-foreground truncate">{b.author}</span>
                      </span>
                      {b.status !== "published" && (
                        <Badge variant="outline" className="text-[10px] shrink-0">{b.status}</Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="flex items-start gap-2.5 text-sm cursor-pointer">
              <input
                type="checkbox" checked={form.send}
                onChange={(e) => setForm({ ...form, send: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-brand-500"
              />
              <span className="text-muted-foreground">
                Email the code to the buyer now.
                <span className="block text-xs mt-0.5">
                  If sending is not set up yet, the code is still created and you can copy the link.
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              onClick={create} disabled={busy || !form.bookIds.length}
              className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : "Create code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* issued */}
      <Dialog open={!!issued} onOpenChange={(o) => !o && setIssued(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Code ready</DialogTitle>
          </DialogHeader>
          {issued && (
            <div className="space-y-4 py-2">
              <div className="rounded-xl border border-border bg-secondary/50 p-4 text-center">
                <p className="font-mono text-2xl font-semibold tracking-[0.16em]">{issued.code}</p>
                <p className="text-xs text-muted-foreground mt-2 break-all">{issued.url}</p>
              </div>

              {issued.sent ? (
                <p className="flex items-center gap-2 text-sm text-emerald-400">
                  <Check className="w-4 h-4" /> Emailed to {issued.email}
                </p>
              ) : (
                <div className="flex items-start gap-2.5 rounded-lg border border-border bg-card/50 px-3.5 py-3">
                  <AlertCircle className="w-4 h-4 text-brand-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    {issued.sendError || "Not emailed."} Send the link above to the buyer yourself.
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <CopyButton value={issued.url} />
                <CopyButton value={issued.code} label="Copy code" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIssued(null)} className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
